import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useStandardsFetch } from "./hooks";
import {
  IStandardsSearchContext,
  standardsSearchProviderInitialValue,
  TOTAL_RESULTS_LIMIT_PER_PAGE,
} from "./StandardsSearchProvider.constants";

const StandardsSearchContext = createContext<IStandardsSearchContext>(
  standardsSearchProviderInitialValue,
);

export const useStandardsSearchContext = () =>
  useContext(StandardsSearchContext);

interface StandardsSearchProviderProps {
  children: ReactElement;
}

const StandardsSearchProvider = ({
  children,
}: StandardsSearchProviderProps) => {
  const [standardsSearchOptions, setStandardsSearchOptions] = useState<{
    currentPage: number;
    currentNeedle: string;
    shouldFetchStandards: boolean;
  }>({
    currentPage: 1,
    currentNeedle: "",
    shouldFetchStandards: false,
  });

  const setCurrentNeedle = useCallback(
    (currentNeedle: string) =>
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentNeedle,
        shouldFetchStandards: true,
      }),
    [standardsSearchOptions],
  );

  const {
    allStandardsModalResults,
    selectedStandards,
    updateSelectedStandardsOnChange,
    top5Standards,
    totalStandardsResults,
    isLoadingStandardsResults,
    fetchFilteredStandards,
    standardsInQuery,
    searchParamsObj,
    resetStandardsList,
    setSelectedStandards,
    getTemporarySelectedFilters,
  } = useStandardsFetch(
    standardsSearchOptions.currentPage,
    standardsSearchOptions.currentNeedle,
  );

  const handleViewMoreStandards = useCallback(() => {
    const { currentPage, currentNeedle } = standardsSearchOptions;
    setStandardsSearchOptions({
      ...standardsSearchOptions,
      currentPage: currentPage + 1,
    });
    fetchFilteredStandards({
      page: currentPage + 1,
      standard_needle: currentNeedle,
    });
  }, [searchParamsObj, standardsSearchOptions]);

  const handleStandardsSearch = useCallback(
    (needle: string) => {
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentNeedle: needle,
        currentPage: 1,
      });
      fetchFilteredStandards({
        standard_needle: needle,
        page: 1,
      });
    },
    [searchParamsObj, standardsSearchOptions],
  );

  const resetStandardsPagination = useCallback(() => {
    const { shouldFetchStandards, currentNeedle } = standardsSearchOptions;
    if (shouldFetchStandards) {
      fetchFilteredStandards({
        standard_needle: currentNeedle,
        page: 1,
      });
      setStandardsSearchOptions({
        ...standardsSearchOptions,
        currentPage: 1,
      });
    }
  }, [searchParamsObj, standardsSearchOptions]);

  const resetStandardsSearch = () => {
    resetStandardsList();
    setStandardsSearchOptions({
      currentNeedle: "",
      currentPage: 1,
      shouldFetchStandards: false,
    });
  };

  const showViewMoreButton = useMemo(
    () =>
      allStandardsModalResults &&
      !isLoadingStandardsResults &&
      standardsSearchOptions.currentPage <
        Math.ceil(totalStandardsResults / TOTAL_RESULTS_LIMIT_PER_PAGE),
    [
      allStandardsModalResults,
      standardsSearchOptions.currentPage,
      isLoadingStandardsResults,
      totalStandardsResults,
    ],
  );

  useEffect(() => {
    resetStandardsPagination();
  }, [standardsSearchOptions.currentNeedle]);

  return (
    <StandardsSearchContext.Provider
      value={{
        top5Standards,
        selectedStandards,
        updateSelectedStandardsOnChange,
        allStandardsModalResults,
        standardsInQuery,
        totalStandardsResults,
        fetchFilteredStandards,
        isLoadingStandardsResults,
        handleViewMoreStandards,
        currentNeedle: standardsSearchOptions.currentNeedle,
        setCurrentNeedle,
        currentPage: standardsSearchOptions.currentPage,
        handleStandardsSearch,
        resetStandardsSearch,
        showViewMoreButton,
        setSelectedStandards,
        getTemporarySelectedFilters,
      }}
    >
      {children}
    </StandardsSearchContext.Provider>
  );
};

export { StandardsSearchProvider };
